import api from '@/lib/services/general.service'

export const state = {
  isAuthenticated: false,
  currentUser: null,
  userId: null,
  profilePhoto: null,
};

export const getters = {
  user: s => s.currentUser?.profile,
  isAuthenticated: s => s.isAuthenticated,
  // Note: With BFF cookie-based auth, tokens are stored server-side
  // These getters return null - authentication is handled via cookies
  access_token: s => s.currentUser?.access_token,
  id_token: s => s.currentUser?.id_token,
  profilePhoto: s => s.profilePhoto,
  isPro: s => {
    const profile = s.currentUser?.profile
    return (
      profile?.subscription_plan?.toLowerCase() === 'pro' &&
      profile?.subscription_status?.toLowerCase() === 'active'
    )
  },
  subscriptionDaysLeft: s => {
    const end = s.currentUser?.profile?.subscription_period_end
    if (!end) return null
    const diff = new Date(end) - Date.now()
    return Math.ceil(diff / (1000 * 60 * 60 * 24))
  },
  isSubscriptionExpiringSoon: s => {
    const end = s.currentUser?.profile?.subscription_period_end
    if (!end) return false
    const daysLeft = Math.ceil((new Date(end) - Date.now()) / (1000 * 60 * 60 * 24))
    return daysLeft >= 0 && daysLeft <= 3
  },
};

export const actions = {
  async fetchUser({ commit, dispatch }, user) {
    commit('SET_LOGGED_IN', user !== null);
    if (user) {
      commit('SET_USER', user);
      dispatch('updateProfilePhoto');
      // Session claims are frozen at login, so a name/photo changed on the IDS account page
      // wouldn't show until re-login. Pull the live profile and merge it in.
      dispatch('refreshProfile');
    } else {
      commit('SET_USER', null);
      commit('UPDATE_PROFILE_PHOTO', null);
    }
  },
  async refreshProfile({ commit, dispatch }) {
    try {
      const response = await api.get('user/profile', {});
      const profile = response?.data?.data ?? response?.data;
      if (profile) {
        commit('MERGE_PROFILE', profile);
        dispatch('updateProfilePhoto');
      }
    } catch {
      // Non-fatal: fall back to the claims already in the session.
    }
  },
  // eslint-disable-next-line no-unused-vars
  async changePassword({ state }, request) {
    const model = {
      password: request.password,
      confirmPassword: request.confirmPassword,
    };
    return await api.insert('user/changepassword', model).then(
      async response => {
        if (response) {
          return response;
        }
      });
  },
  updateProfilePhoto({ commit, state }) {
    // Prefer the avatar from the IDS: Zitadel sends it as the `picture` claim, so a photo
    // the user sets on their account page shows up here on the next login/refresh.
    const picture = state.currentUser?.profile?.picture;
    if (picture) {
      commit('UPDATE_PROFILE_PHOTO', picture);
      return;
    }
    // Fallback: legacy CDN convention (only when a CDN is actually configured — otherwise
    // this produced an "undefined/..." URL that never resolved).
    const cdn = process.env.VUE_APP_IMAGE_CDN;
    commit('UPDATE_PROFILE_PHOTO', cdn ? `${cdn}/img/user/${state.userId}.jpg?${Date.now()}` : null);
  },
};

export const mutations = {
  SET_LOGGED_IN(state, value) {
    state.isAuthenticated = value;
  },
  SET_USER(state, info) {
    state.currentUser = info;
    if (info) {
      // state.userInfo = info.userInfo;
      state.userId = info.profile.sub;
      // state.role = info.userInfo;
    }
  },
  UPDATE_PROFILE_PHOTO(state, photo) {
    state.profilePhoto = photo;
  },
  // Merge live IDS profile fields (name/picture/email) over the frozen session claims.
  MERGE_PROFILE(state, profile) {
    if (!state.currentUser?.profile) return;
    state.currentUser = {
      ...state.currentUser,
      profile: { ...state.currentUser.profile, ...profile },
    };
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
